import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Story {
  id: number;
  name: string;
  age: number;
  location: string;
  category: string;
  story: string;
  impact: string;
  image: string;
  alt: string;
  date: string;
}

const BeneficiaryStoriesSection = () => {
  const stories: Story[] = [
  {
    id: 1,
    name: 'Amina Hassan',
    age: 34,
    location: 'Mogadishu, Somalia',
    category: 'Food Security',
    story: 'After years of drought devastated our community, I struggled to feed my five children. The emergency food distribution program provided not just meals, but hope. Now my children attend school with full stomachs, and I\'ve learned sustainable farming techniques to grow our own vegetables.',
    impact: 'Family of 7 supported for 6 months',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1557c3ee6-1764807754890.png",
    alt: 'African woman in colorful headscarf smiling while holding fresh vegetables from garden',
    date: 'January 2026'
  },
  {
    id: 2,
    name: 'Carlos Mendez',
    age: 42,
    location: 'Guatemala City, Guatemala',
    category: 'Healthcare',
    story: 'When my daughter fell seriously ill, we had no access to medical care. The mobile health clinic came to our village and saved her life. The doctors didn\'t just treat her illness - they taught us about preventive care and nutrition. Today, she\'s healthy and thriving.',
    impact: 'Critical medical intervention provided',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f147df5d-1763301649610.png",
    alt: 'Hispanic father holding young daughter in medical clinic with doctor in background',
    date: 'December 2025'
  },
  {
    id: 3,
    name: 'Fatima Al-Rashid',
    age: 28,
    location: 'Aleppo, Syria',
    category: 'Education',
    story: 'The war destroyed our schools and my dreams of becoming a teacher. The education support program rebuilt our community center and provided teaching materials. Now I teach 40 children who had lost years of education. We\'re rebuilding not just buildings, but futures.',
    impact: '40 children returned to education',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b80005d4-1764819940181.png",
    alt: 'Middle Eastern woman in hijab teaching children in classroom with books and learning materials',
    date: 'November 2025'
  },
  {
    id: 4,
    name: 'Rajesh Kumar',
    age: 51,
    location: 'Bihar, India',
    category: 'Clean Water',
    story: 'For decades, our village walked 3 kilometers daily for contaminated water. Many children suffered from waterborne diseases. The clean water infrastructure project installed a well and filtration system. Now we have safe water at our doorstep, and disease rates have dropped by 80%.',
    impact: 'Village of 2,400 people served',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b1503dc-1767725029470.png",
    alt: 'Indian man in traditional clothing standing next to new water pump with children collecting clean water',
    date: 'October 2025'
  },
  {
    id: 5,
    name: 'Maria Santos',
    age: 38,
    location: 'Manila, Philippines',
    category: 'Emergency Shelter',
    story: 'The typhoon destroyed everything we owned. We lived under tarps for weeks until the emergency shelter program provided us with a safe, weatherproof home. More than shelter, they gave us dignity and a foundation to rebuild our lives. My children can sleep safely again.',
    impact: 'Family of 6 housed securely',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12f2303b2-1769697974363.png",
    alt: 'Filipino woman with children standing in front of newly constructed emergency shelter home',
    date: 'September 2025'
  },
  {
    id: 6,
    name: 'Ahmed Ibrahim',
    age: 45,
    location: 'Khartoum, Sudan',
    category: 'Livelihood',
    story: 'Conflict forced us to flee our farm and lose our income. The agricultural recovery program provided seeds, tools, and training in drought-resistant farming. Today, I not only feed my family but sell surplus crops at the market. We\'ve regained our independence and pride.',
    impact: 'Sustainable income restored',
    image: "https://images.unsplash.com/photo-1709532919690-4a8fa05b7495",
    alt: 'Sudanese farmer in traditional clothing working in green agricultural field with crops',
    date: 'August 2025'
  }];


  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            Stories of Transformation
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Behind every statistic is a human story. Meet the individuals and families whose lives have been transformed through compassionate action and community support.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stories.map((story) =>
          <div
            key={story.id}
            className="bg-card rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-border">

              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={story.image}
                alt={story.alt}
                className="w-full h-full object-cover" />

                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-cta font-bold">
                    {story.category}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-headline font-bold text-foreground mb-1">
                      {story.name}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Icon name="MapPinIcon" size={14} className="text-primary" variant="solid" />
                      <span className="font-body">{story.location}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-cta font-bold text-foreground">
                      Age {story.age}
                    </div>
                    <div className="text-xs text-muted-foreground font-body">
                      {story.date}
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-foreground font-body leading-relaxed line-clamp-6">
                    &quot;{story.story}&quot;
                  </p>
                </div>

                <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="ChartBarIcon" size={18} className="text-primary" variant="solid" />
                    <span className="text-sm font-cta font-bold text-primary">
                      Impact: {story.impact}
                    </span>
                  </div>
                </div>

                <button className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-sm transition-all duration-300 hover:bg-primary/90 flex items-center justify-center space-x-2">
                  <Icon name="BookOpenIcon" size={18} variant="solid" />
                  <span>Read Full Story</span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-12">
          <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg flex items-center space-x-2 mx-auto">
            <Icon name="UserGroupIcon" size={24} variant="solid" />
            <span>View All Stories</span>
          </button>
        </div>
      </div>
    </section>);

};

export default BeneficiaryStoriesSection;